/**
 * A refusal from `administration-server`, named.
 *
 * The server answers every refusal, from every layer, with a JSON object whose
 * one guaranteed key is `code`. That contract is why this is its own class
 * rather than the sibling SDK's `ApiError`: that one fronts `vibe-server`, whose
 * refusals read `{ error: { code, message, details, requestId } }`. Two servers,
 * two shapes, and a client that flattened them into one would make every caller
 * guess which it had.
 *
 * `code` is what a caller branches on and it is always there. The other three
 * are present only when the server sent them, so `Object.keys(err)` reads what
 * actually arrived rather than a row of phantom `undefined`s:
 *
 * | Field | Sent by |
 * | --- | --- |
 * | `detail` | a refusal that names the thing — `DUPLICATE_UNIQUE_KEY` names the index, `INVALID_SEARCH_FIELD` names the field |
 * | `diagnostics` | `INVALID_SEARCH_QUERY` alone: coded, spanned FQL problems, each one a character range a search bar underlines |
 * | `validation` | `REQUEST_VALIDATION_FAILED` alone: which of body, params, querystring or headers failed, and how |
 *
 * `path` is the field a logging seam wants. A URL carries a query string and a
 * per-record identifier, and logging it raw yields one "endpoint" per company.
 * Collapsing identifiers is the logger's business, but it should not have to
 * re-parse a URL to do it.
 */

/**
 * The path a URL names, with the query and fragment dropped and an absolute URL
 * reduced to its path.
 *
 * Deliberately not the `URL` constructor: the base may be relative (a dev
 * proxy's `/api`), which the constructor rejects without a base, and the only
 * base available in a browser is `window.location.origin` — which is what makes
 * a module browser-only. String work has neither problem and runs anywhere.
 */
export function requestPath(url) {
    const withoutQuery = String(url).split('?')[0].split('#')[0];
    const schemeEnd = withoutQuery.indexOf('://');
    if (schemeEnd === -1) {
        return withoutQuery;
    }
    const afterAuthority = withoutQuery.slice(schemeEnd + 3);
    const firstSlash = afterAuthority.indexOf('/');
    if (firstSlash === -1) {
        return '/';
    }
    return afterAuthority.slice(firstSlash);
}

// What a caller gets when the server refused but sent nothing readable — a
// proxy's HTML error page, or a body that is not JSON. The generic half of the
// server's own contract uses this code for anything uncaught, so a caller that
// branches on it needs no separate case for "the server did not answer in its
// own contract".
const UNREADABLE_REFUSAL = 'INTERNAL_ERROR';

export class AdministrationApiError extends Error {
    constructor({ method, url, statusCode, body }) {
        const path = requestPath(url);
        const code = typeof body?.code === 'string' ? body.code : UNREADABLE_REFUSAL;
        super(`${code} (${method} ${path}) — ${statusCode}`);
        this.name = 'AdministrationApiError';
        this.code = code;
        this.statusCode = statusCode;
        this.method = method;
        this.url = String(url);
        this.path = path;
        if (typeof body?.detail === 'string') {
            this.detail = body.detail;
        }
        if (Array.isArray(body?.diagnostics)) {
            this.diagnostics = body.diagnostics;
        }
        if (body?.validation !== undefined) {
            this.validation = body.validation;
        }
        // Kept whole beside the fields read off it. The contract guarantees
        // `code` and nothing else, so a caller meeting a refusal this class does
        // not model yet can still read what arrived instead of losing it.
        if (body !== undefined) {
            this.body = body;
        }
    }
}

export default AdministrationApiError;
